import { clampPercent } from '../utils/clamp-percent';
import { clampUnitInterval } from '../utils/clamp-unit-interval';
import { parseFiniteNumber } from '../utils/parse-finite-number';
import { roundTo } from '../utils/round-to';

export const gradientModelManager = Object.freeze({
  extractLinearAngle(model) {
    if (
      model.orientation &&
      !Array.isArray(model.orientation) &&
      model.orientation.type === 'angular'
    ) {
      return parseFiniteNumber(model.orientation.value, 0);
    }

    return 0;
  },

  extractRadialPosition(model) {
    if (Array.isArray(model.orientation)) {
      const shape = model.orientation.find((item) => item.type === 'shape');

      if (shape?.at?.type === 'position') {
        return {
          x: parseFiniteNumber(shape.at.value.x.value, 50),
          y: parseFiniteNumber(shape.at.value.y.value, 50),
        };
      }
    }

    return { x: 50, y: 50 };
  },

  extractRadialEndingShape(model) {
    if (Array.isArray(model.orientation)) {
      const shape = model.orientation.find((item) => item.type === 'shape');

      if (shape?.value) {
        return shape.value;
      }
    }

    return 'circle';
  },

  extractStopPosition(stop) {
    return parseFiniteNumber(stop.length.value, 0);
  },

  extractStopColor(stop) {
    const [h, s, l, a] = stop.value;

    return {
      h: parseFiniteNumber(h, 0),
      s: clampUnitInterval(parseFiniteNumber(s, 0)),
      l: clampUnitInterval(parseFiniteNumber(l, 0)),
      a: parseFiniteNumber(a, 1),
    };
  },

  buildLengthPercentage(value) {
    return {
      type: '%',
      value: String(roundTo(clampPercent(value), 2)),
    };
  },

  buildAngularOrientation(angle) {
    return {
      type: 'angular',
      value: String(roundTo(angle, 2)),
      unit: 'deg',
    };
  },

  buildShapeOrientation(position, shape) {
    return [
      {
        type: 'shape',
        value: shape,
        at: {
          type: 'position',
          value: {
            x: gradientModelManager.buildLengthPercentage(position.x),
            y: gradientModelManager.buildLengthPercentage(position.y),
          },
        },
      },
    ];
  },

  switchType(prev, nextType) {
    if (prev.type === nextType) {
      return null;
    }

    const colorStops = prev.colorStops.map((stop) => ({
      ...stop,
      length: gradientModelManager.buildLengthPercentage(
        gradientModelManager.extractStopPosition(stop)
      ),
    }));

    switch (nextType) {
      case 'radial-gradient':
        return {
          type: nextType,
          orientation: gradientModelManager.buildShapeOrientation(
            gradientModelManager.extractRadialPosition(prev),
            'circle'
          ),
          colorStops,
        };
      case 'linear-gradient':
        return {
          type: nextType,
          orientation: gradientModelManager.buildAngularOrientation(
            gradientModelManager.extractLinearAngle(prev)
          ),
          colorStops,
        };
      default:
        return null;
    }
  },

  buildHslaValue(color) {
    return [
      String(roundTo(color.h, 2)),
      String(roundTo(clampUnitInterval(color.s), 4)),
      String(roundTo(clampUnitInterval(color.l), 4)),
      String(roundTo(clampUnitInterval(color.a), 3)),
    ];
  },
});
